import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { viewportConfig, staggerContainer } from './animations';
import type { Variants } from 'framer-motion';
import { MessageCircle, Send, MessageSquare, Phone, Hash, Globe } from 'lucide-react';

const channels = [
  { id: 'whatsapp', color: 'border-t-green-500', icon: MessageCircle, iconBg: 'bg-green-500' },
  { id: 'telegram', color: 'border-t-blue-500', icon: Send, iconBg: 'bg-blue-500' },
  { id: 'discord', color: 'border-t-indigo-500', icon: MessageSquare, iconBg: 'bg-indigo-500' },
  { id: 'line', color: 'border-t-emerald-600', icon: Phone, iconBg: 'bg-emerald-600' },
  { id: 'slack', color: 'border-t-purple-500', icon: Hash, iconBg: 'bg-purple-500' },
  { id: 'web', color: 'border-t-cyan-500', icon: Globe, iconBg: 'bg-cyan-500' },
];

export function ChannelsSection() {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-28 bg-muted/30">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportConfig}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landing.channels.title')}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('landing.channels.subtitle')}</p>
        </motion.div>

        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={viewportConfig}
        >
          {channels.map((ch, i) => {
            const fromLeft = i % 2 === 0;
            const itemVariant: Variants = {
              initial: { opacity: 0, x: fromLeft ? -30 : 30 },
              animate: { opacity: 1, x: 0, transition: { duration: 0.5 } },
            };

            const ChannelIcon = ch.icon;

            return (
              <motion.div key={ch.id} variants={itemVariant}>
                <Card className={`h-full border-t-4 ${ch.color} hover:shadow-lg hover:-translate-y-1 transition-all duration-300`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`h-10 w-10 rounded-lg ${ch.iconBg} flex items-center justify-center text-white font-bold`}>
                        <ChannelIcon className="h-5 w-5" />
                      </div>
                      <h3 className="text-lg font-semibold">{t(`landing.channels.${ch.id}.name`)}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{t(`landing.channels.${ch.id}.description`)}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
